import { useAuth } from '@lastbrand/fe-common/universal-auth'
import { useEffect, useRef, useState } from 'react'

import { useStatsigInitContext } from '@src/experiments/statsig-integration/statsig.context'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { ErrorScreen, LoaderWrapper } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import orderAggregatorService from '@src/quince-site-common/src/services/orderAggregatorService'
import { SearchType } from '@src/quince-site-common/src/services/orderAggregatorService/orderAggregatorService.types'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import SearchOptions from './components/SearchOptions'
import { STATSIG_FLAG_FIND_MY_ORDER_GUEST } from './utils'

export default function FindMyOrder() {
  const [loading, setLoading] = useState<boolean>(true)
  const [searchOptions, setSearchOptions] = useState<SearchType[]>([])
  const [error, setError] = useState<boolean>(false)
  const hasFetchedRef = useRef<boolean>(false)

  const router = useRouter()
  const isNativeViewClient = getIsNativeViewClient()
  const { isLoggedIn, isInitializing } = useAuth()
  const { init: isStatsigReady } = useStatsigInitContext()
  const enableFindMyOrder = useStatsigDynamicConfig<boolean>('enableFindMyOrder')
  const enableFindMyOrderOnApp = useStatsigDynamicConfig<boolean>('enableFindMyOrderOnApp')
  const enableFMOForGuestUser = useStatsigDynamicConfig<boolean>(STATSIG_FLAG_FIND_MY_ORDER_GUEST)

  const fetchSearchTypes = async () => {
    try {
      const data = await orderAggregatorService.getAllSearchTypes()
      setSearchOptions(data?.searchTypes ?? [])
    } catch (err) {
      setError(true)
      captureException(new Error('Error fetching search types'), {
        feature: FEATURES.FIND_MY_ORDER,
        module: MODULES.FIND_MY_ORDER,
        extraInfo: {
          message: 'Error fetching search types',
          error: err,
        },
      })
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (isInitializing || !isStatsigReady) return

    const activeFlag = isNativeViewClient
      ? enableFindMyOrderOnApp
      : isLoggedIn
        ? enableFindMyOrder
        : enableFMOForGuestUser

    if (activeFlag === undefined) return

    if (!activeFlag) {
      if (isLoggedIn) {
        router.push('/account/my-orders-returns')
      } else {
        router.push('/log-in')
      }
    } else if (!hasFetchedRef.current) {
      hasFetchedRef.current = true
      void fetchSearchTypes()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- router and isNativeViewClient are stable for the page lifetime
  }, [enableFindMyOrder, enableFindMyOrderOnApp, enableFMOForGuestUser, isLoggedIn, isInitializing, isStatsigReady])

  return (
    <div>
      {loading ? (
        <LoaderWrapper showLoader={loading} />
      ) : error ? (
        <ErrorScreen />
      ) : (
        <SearchOptions searchOptions={searchOptions} />
      )}
    </div>
  )
}
